import dayjs from '../../utils/dayjs';
import { changeOrderItemSize } from '../../components/product/useOrderItemMutation';
import { HistoricalOrderItem } from '../order/HistoricalOrderItem';
import { OrderItem } from '../order/OrderItem';
import { HistoricalOrder } from './HistoricalOrder';
import type { CustomerOrderDetailsDto } from '@kotipizzagroup/kotipizza-users-api-client';
import type { IngredientWithDosing, ProductItem } from '../../components/shoppingCart/types';

export class OrderHistory {
  private orders: HistoricalOrder[] = [];

  public static fromOrderHistoryData(
    data: CustomerOrderDetailsDto[],
    products: ProductItem[],
    allIngredients: IngredientWithDosing[]
  ): OrderHistory {
    const history = new OrderHistory();

    data.forEach((order) => {
      const orderItems = order.orderItems.map((historicalItem) => {
        const productItem = products.find(
          (productItem) =>
            productItem.productID === historicalItem.productId &&
            productItem.sizeId === historicalItem.sizeId
        );

        let item: HistoricalOrderItem = new HistoricalOrderItem();

        if (!productItem) {
          item.inactive = true;
          item.name = `${historicalItem.name} (poistunut valikoimasta)`;
        } else {
          const { item: newItem } = changeOrderItemSize(
            OrderItem.fromProductItem(productItem),
            products,
            allIngredients,
            historicalItem.sizeId
          );

          item = HistoricalOrderItem.fromOrderItem(newItem);

          if (
            dayjs(order.orderPlacedAt).isBefore(dayjs(productItem.updated)) &&
            productItem.ingredients.length > 1
          ) {
            const historicalItemRecipe = historicalItem.ingredients
              .filter((i) => i.recipeQuantity > 0)
              .map((i) => i.ingredientID)
              .sort()
              .toString();

            const productItemRecipe = productItem.ingredients
              .filter((i) => i.quantity > 0)
              .map((i) => i.ingredientID)
              .sort()
              .toString();

            if (historicalItemRecipe !== productItemRecipe) {
              item.recipeOutdated = true;
            }
          }
        }

        const ingredients = historicalItem.ingredients.map((historicalIngredient) => {
          let ingredient: IngredientWithDosing | undefined = allIngredients.find(
            (i) =>
              i.ingredientID === historicalIngredient.ingredientID &&
              i.productSizeId === historicalItem.sizeId
          );

          if (!ingredient) {
            ingredient = {
              displayOrder: 0,
              productSizeId: historicalItem.sizeId,
              dosingInGrams: 0,
              maxQuantity: 0,
              minQuantity: 0,
              price: 0,
              inactive: true,
              ingredientID: 0,
              quantity: 0,
              ingredientCategoryID: 0,
              name: 'Tuntematon täyte',
              ingredientdosings: [],
              dietInformation: {
                isSpicy: false,
                isGlutenFree: false,
                isSoyFree: false,
                isVegan: false,
                isVegetarian: false,
                isEggless: false,
                isDairyFree: false,
                isLactoseFree: false,
              },
            };
          }

          return {
            ...ingredient,
            quantity: historicalIngredient.quantity,
          };
        });

        item.setIngredients(ingredients);

        item.price = historicalItem.itemPrice;
        item.discountedPrice = historicalItem.discountedPrice;
        item.discount = historicalItem.discount;
        item.discountedProductQuantity = historicalItem.discountedProductQuantity;
        item.quantity = historicalItem.quantity;

        return item;
      });

      const newOrder = new HistoricalOrder(order, orderItems);

      if (newOrder.orderItems.length > 0) {
        history.addOrder(newOrder);
      }
    });

    history.sortOrders();
    return history;
  }

  public getActiveOrders(): HistoricalOrder[] {
    return this.orders.filter((o) => !o.includesInactiveProducts());
  }

  public getAllOrders(): HistoricalOrder[] {
    return this.orders;
  }

  public addOrder(order: HistoricalOrder): void {
    this.orders.push(order);
  }

  public getAllItems(): OrderItem[] {
    return this.orders.reduce(
      (all: OrderItem[], current) => [...all, ...current.orderItems],
      []
    ) as OrderItem[];
  }

  public getAllItemsWithSingleQuantity(): OrderItem[] {
    return this.getAllItems().map((item) => {
      const newItem = item.clone();
      newItem.quantity = 1;
      newItem.price = item.price / item.quantity;
      return newItem;
    });
  }

  public getRecommendedProducts(amount?: number): OrderItem[] {
    const items: OrderItem[] = this.getAllItemsWithSingleQuantity();

    let uniqueItems = items.filter((item, index) => {
      const firstIndex = items.findIndex((itemToFind) => itemToFind.equals(item));
      return index === firstIndex;
    });

    if (amount) {
      uniqueItems = uniqueItems.slice(0, amount);
    }

    return uniqueItems;
  }

  public sortOrders(): void {
    this.orders.sort((a, b) => {
      return a.orderPlacedAt.isBefore(b.orderPlacedAt) ? 1 : -1;
    });
  }
}
